import type { Disposable } from "@/shared/contracts/lifecycle";
import banIconSource from "bootstrap-icons/icons/ban.svg?raw";
import pauseIconSource from "bootstrap-icons/icons/pause-fill.svg?raw";
import saveIconSource from "bootstrap-icons/icons/save.svg?raw";
import { getMessages } from "@/i18n/runtime";
import type { HoverIdleSnapshot } from "@/features/hoverIdle/types";
import type { HoverPointerModel } from "@/features/hoverIdle/input/hoverPointerModel";
import {
  MPM_HOV_TARGET_EXPANDED_SLOT_COUNT,
  createMpmHovTargetSlots,
  resolveMpmHovTargetSlotCount,
} from "@/features/hoverIdle/mpm/mpmHovTargetSlots";
import {
  createMpmHovGainUpgradeSlots,
  type HovGainUpgradeMpTarget,
} from "@/features/hoverIdle/mpm/mpmHovGainUpgradeSlots";
import {
  createMpmAmplifierPanelSlots,
  createMpmAmplifierUpgradeSlots,
} from "@/features/hoverIdle/mpm/mpmAmplifierPanelSlots";
import {
  resolveMpmAmplifierPanelAssignAllState,
  resolveMpmExplorationAssignAllState,
  resolveMpmHovGainUpgradesAssignAllState,
  resolveMpmHovTargetsAssignAllState,
} from "@/features/hoverIdle/mpm/mpmAssignAll";
import { MpSlotChargeController } from "@/features/hoverIdle/mpm/mpSlotChargeController";
import {
  createEnabledHoverToggle,
  type EnabledHoverToggle,
} from "@/features/hoverIdle/mpm/enabledHoverToggle";
import { MpmHeaderActionChargeController } from "@/features/hoverIdle/mpm/mpmHeaderActionChargeController";
import { computeMpmAvailableMpFromOccupancy } from "@/features/hoverIdle/mpm/mpmCapacity";
import {
  MPM_MHOV_EXECUTION_MP_COST,
  MPM_NHOV_EXECUTION_MP_COST_PER_BRANCH,
  MPM_OHOV_EXECUTION_MP_COST,
  MPM_PRESET_BASE_SLOT_COUNT,
  MPM_PRESET_MAX_SLOT_COUNT,
  type MpmPresetSnapshot,
} from "@/features/hoverIdle/mpm/mpmState";
import { computeMpmPresetAssignedMp } from "@/features/hoverIdle/mpm/mpmPresets";
import { createMpSlotIndicator } from "@/features/hoverIdle/mpm/mpSlotView";
import { resolveDirectMpTargetViewState } from "@/features/hoverIdle/mpm/directMpTargetViewState";
import type { DirectMpBlockedReason } from "@/features/hoverIdle/mpm/directMpBlockState";
import {
  HOV_AMPLIFIER_ENTRY_DEFINITIONS,
  HOV_AMPLIFIER_SHORT_LABELS,
  type HovAmplifierEntryId,
} from "@/features/hoverIdle/hov/hovAmplifierModel";
import { NHOV_BRANCH_IDS } from "@/features/hoverIdle/nHov/nHovBranches";
import type { NHovBranchId } from "@/features/hoverIdle/types";
import { EXPLORATION_MP_SLOT_SEQUENCE } from "@/features/hoverIdle/exploration/explorationDefinitions";
import {
  EXPLORATION_BIOME_IDS,
  type ExplorationBiomeId,
  type ExplorationMpSlotDefinition,
  type ExplorationMpSlotId,
} from "@/features/hoverIdle/exploration/explorationTypes";

interface MpmViewOptions {
  readonly container: HTMLElement;
  readonly pointerModel: HoverPointerModel;
  readonly onMpAssign: (cellIndex: number) => void;
  readonly onMpRemove: (cellIndex: number) => void;
  readonly onHovGainUpgradeMpAssign: (target: HovGainUpgradeMpTarget) => void;
  readonly onHovGainUpgradeMpRemove: (target: HovGainUpgradeMpTarget) => void;
  readonly onAmplifierPanelMpAssign: (entryId: HovAmplifierEntryId) => void;
  readonly onAmplifierPanelMpRemove: (entryId: HovAmplifierEntryId) => void;
  readonly onAmplifierUpgradeMpAssign: (entryId: HovAmplifierEntryId) => void;
  readonly onAmplifierUpgradeMpRemove: (entryId: HovAmplifierEntryId) => void;
  readonly onExplorationMpAssign: (slotId: ExplorationMpSlotId) => void;
  readonly onExplorationMpRemove: (slotId: ExplorationMpSlotId) => void;
  readonly onMHovExecutionMpAssign: () => void;
  readonly onMHovExecutionMpRemove: () => void;
  readonly onNHovExecutionMpAssign: (branchId: NHovBranchId) => void;
  readonly onNHovExecutionMpRemove: (branchId: NHovBranchId) => void;
  readonly onOHovExecutionMpAssign: () => void;
  readonly onOHovExecutionMpRemove: () => void;
  readonly onMHovExecutionMultiplierSave: (value: string) => void;
  readonly onMHovExecutionMinimumResetIntervalMsSave: (value: number) => void;
  readonly onNHovExecutionMinimumEffectSave: (
    branchId: NHovBranchId,
    value: string,
  ) => void;
  readonly onNHovExecutionMinimumTimeSecondsSave: (
    branchId: NHovBranchId,
    value: number,
  ) => void;
  readonly onOHovExecutionMinimumHovCoreSave: (value: string) => void;
  readonly onOHovExecutionMinimumIntervalMsSave: (value: number) => void;
  readonly onMHovExecutionEnabledChange: (enabled: boolean) => void;
  readonly onNHovExecutionEnabledChange: (enabled: boolean) => void;
  readonly onOHovExecutionEnabledChange: (enabled: boolean) => void;
  readonly onHovTargetsClearAll: () => void;
  readonly onHovGainUpgradesClearAll: () => void;
  readonly onAmplifierPanelsClearAll: () => void;
  readonly onAmplifierUpgradesClearAll: () => void;
  readonly onExplorationClearAll: () => void;
  readonly onHovTargetsAssignAll: () => void;
  readonly onHovGainUpgradesAssignAll: () => void;
  readonly onAmplifierPanelsAssignAll: () => void;
  readonly onExplorationAssignAll: () => void;
  readonly onHovTargetsEnabledChange: (enabled: boolean) => void;
  readonly onHovGainUpgradesEnabledChange: (enabled: boolean) => void;
  readonly onAmplifierPanelsEnabledChange: (enabled: boolean) => void;
  readonly onAmplifierUpgradesEnabledChange: (enabled: boolean) => void;
  readonly onExplorationEnabledChange: (enabled: boolean) => void;
  readonly onPresetSave: (presetIndex: number) => void;
  readonly onPresetLoad: (presetIndex: number) => void;
  readonly getMpmPresetSlotCount?: () => number;
  readonly getHoverToggleDurationMs?: () => number;
  readonly getHoverChargeDurationMs: () => number;
}

interface MpmAssignableSlotNodes {
  readonly element: HTMLDivElement;
  readonly indicator: HTMLDivElement;
  readonly controller: MpSlotChargeController;
  isAssigned: boolean;
  isDisabled: boolean;
}

interface MpmMissingMpTooltipNodes {
  readonly cleanup: () => void;
  readonly tooltip: HTMLDivElement;
}

interface MpmExecutionSlotNodes extends MpmAssignableSlotNodes {
  readonly missingMpTooltip: MpmMissingMpTooltipNodes;
}

type MpmMHovExecutionSlotNodes = MpmExecutionSlotNodes;

interface MpmNHovExecutionSlotNodes extends MpmExecutionSlotNodes {
  readonly branchId: NHovBranchId;
}

interface MpmHovTargetSlotNodes extends MpmAssignableSlotNodes {
  readonly index: number;
}

interface MpmHovGainUpgradeSlotNodes extends MpmAssignableSlotNodes {
  readonly index: number;
  target: HovGainUpgradeMpTarget | null;
}

interface MpmAmplifierPanelSlotNodes extends MpmAssignableSlotNodes {
  readonly entryId: HovAmplifierEntryId;
}

interface MpmAmplifierUpgradeSlotNodes extends MpmAssignableSlotNodes {
  readonly entryId: HovAmplifierEntryId;
}

interface MpmExplorationSlotNodes extends MpmExecutionSlotNodes {
  readonly slotId: ExplorationMpSlotId;
  readonly biomeId: ExplorationBiomeId;
  readonly requiredMp: number;
}

interface MpmExplorationBiomeNodes {
  readonly biomeId: ExplorationBiomeId;
  readonly element: HTMLDivElement;
}

type MpmAssignmentSectionId =
  | "hov-targets"
  | "hov-gain-upgrades"
  | "mhov-execution"
  | "nhov-execution"
  | "ohov-execution"
  | "exploration"
  | "amplifier-panels"
  | "amplifier-upgrades";

type MpmHeaderActionSectionId = MpmAssignmentSectionId | "presets";

interface MpmHeaderActionNodes {
  readonly element: HTMLButtonElement;
  readonly controller: MpmHeaderActionChargeController;
}

interface MpmPresetSlotNodes {
  readonly element: HTMLDivElement;
  readonly allocation: HTMLDivElement;
  readonly saveAction: MpmHeaderActionNodes;
  readonly loadAction: MpmHeaderActionNodes;
}

type MpmEnabledToggleNodes = EnabledHoverToggle;

interface MpmAssignmentSectionNodes {
  readonly element: HTMLDivElement;
  readonly pauseOverlay: HTMLDivElement;
  readonly blockedOverlay: HTMLDivElement;
}

interface MpmTextInputNodes {
  readonly root: HTMLDivElement;
  readonly label: HTMLLabelElement;
  readonly input: HTMLInputElement;
  readonly saveButton: HTMLButtonElement;
  savedValue: string;
  editing: boolean;
}

interface MpmExecutionConditionInputNodes {
  readonly effect: MpmTextInputNodes;
  readonly interval: MpmTextInputNodes;
}

const MPM_MHOV_EXECUTION_INTERVAL_SECONDS_PER_MS = 1 / 1000;
const MPM_MHOV_EXECUTION_INTERVAL_MS_PER_SECOND = 1000;

function isHoverIdleDocumentFocused(): boolean {
  if (typeof document === "undefined") return false;
  const candidate = (document as { hasFocus?: () => boolean }).hasFocus;
  if (typeof candidate !== "function") return true;
  try {
    return candidate.call(document);
  } catch {
    return true;
  }
}

export class MpmView implements Disposable {
  readonly root: HTMLDivElement;
  readonly hovTargetsContainer: HTMLDivElement;
  private readonly pointerModel: HoverPointerModel;
  private readonly onMpAssign: (cellIndex: number) => void;
  private readonly onMpRemove: (cellIndex: number) => void;
  private readonly onHovGainUpgradeMpAssign: (
    target: HovGainUpgradeMpTarget,
  ) => void;
  private readonly onHovGainUpgradeMpRemove: (
    target: HovGainUpgradeMpTarget,
  ) => void;
  private readonly onAmplifierPanelMpAssign: (
    entryId: HovAmplifierEntryId,
  ) => void;
  private readonly onAmplifierPanelMpRemove: (
    entryId: HovAmplifierEntryId,
  ) => void;
  private readonly onAmplifierUpgradeMpAssign: (
    entryId: HovAmplifierEntryId,
  ) => void;
  private readonly onAmplifierUpgradeMpRemove: (
    entryId: HovAmplifierEntryId,
  ) => void;
  private readonly onExplorationMpAssign: (slotId: ExplorationMpSlotId) => void;
  private readonly onExplorationMpRemove: (slotId: ExplorationMpSlotId) => void;
  private readonly onMHovExecutionMpAssign: () => void;
  private readonly onMHovExecutionMpRemove: () => void;
  private readonly onNHovExecutionMpAssign: (branchId: NHovBranchId) => void;
  private readonly onNHovExecutionMpRemove: (branchId: NHovBranchId) => void;
  private readonly onOHovExecutionMpAssign: () => void;
  private readonly onOHovExecutionMpRemove: () => void;
  private readonly onMHovExecutionMultiplierSave: (value: string) => void;
  private readonly onMHovExecutionMinimumResetIntervalMsSave: (
    value: number,
  ) => void;
  private readonly onNHovExecutionMinimumEffectSave: (
    branchId: NHovBranchId,
    value: string,
  ) => void;
  private readonly onNHovExecutionMinimumTimeSecondsSave: (
    branchId: NHovBranchId,
    value: number,
  ) => void;
  private readonly onOHovExecutionMinimumHovCoreSave: (value: string) => void;
  private readonly onOHovExecutionMinimumIntervalMsSave: (
    value: number,
  ) => void;
  private readonly onMHovExecutionEnabledChange: (enabled: boolean) => void;
  private readonly onNHovExecutionEnabledChange: (enabled: boolean) => void;
  private readonly onOHovExecutionEnabledChange: (enabled: boolean) => void;
  private readonly onHovTargetsClearAll: () => void;
  private readonly onHovGainUpgradesClearAll: () => void;
  private readonly onAmplifierPanelsClearAll: () => void;
  private readonly onAmplifierUpgradesClearAll: () => void;
  private readonly onExplorationClearAll: () => void;
  private readonly onHovTargetsAssignAll: () => void;
  private readonly onHovGainUpgradesAssignAll: () => void;
  private readonly onAmplifierPanelsAssignAll: () => void;
  private readonly onExplorationAssignAll: () => void;
  private readonly onHovTargetsEnabledChange: (enabled: boolean) => void;
  private readonly onHovGainUpgradesEnabledChange: (enabled: boolean) => void;
  private readonly onAmplifierPanelsEnabledChange: (enabled: boolean) => void;
  private readonly onAmplifierUpgradesEnabledChange: (enabled: boolean) => void;
  private readonly onExplorationEnabledChange: (enabled: boolean) => void;
  private readonly onPresetSave: (presetIndex: number) => void;
  private readonly onPresetLoad: (presetIndex: number) => void;
  private readonly getMpmPresetSlotCount: () => number;
  private readonly getHoverToggleDurationMs: (() => number) | null;
  private readonly getHoverChargeDurationMs: () => number;
  private readonly presetSlots: readonly MpmPresetSlotNodes[];
  private readonly hovTargetsSection: MpmAssignmentSectionNodes;
  private readonly hovTargetsAssignAllAction: MpmHeaderActionNodes;
  private readonly hovTargetsClearAllAction: MpmHeaderActionNodes;
  private readonly hovTargetsEnabledToggle: MpmEnabledToggleNodes;
  private readonly hovGainUpgradesSection: MpmAssignmentSectionNodes;
  private readonly hovGainUpgradesAssignAllAction: MpmHeaderActionNodes;
  private readonly hovGainUpgradesClearAllAction: MpmHeaderActionNodes;
  private readonly hovGainUpgradesEnabledToggle: MpmEnabledToggleNodes;
  private readonly amplifierPanelsSection: MpmAssignmentSectionNodes;
  private readonly mhovExecutionSection: MpmAssignmentSectionNodes;
  private readonly mhovExecutionToggle: MpmEnabledToggleNodes;
  private readonly mhovExecutionButton: MpmMHovExecutionSlotNodes;
  private readonly nhovExecutionSection: MpmAssignmentSectionNodes;
  private readonly nhovExecutionToggle: MpmEnabledToggleNodes;
  private readonly nhovExecutionSlots: readonly MpmNHovExecutionSlotNodes[];
  private readonly nhovConditionInputs: Readonly<
    Record<NHovBranchId, MpmExecutionConditionInputNodes>
  >;
  private readonly ohovExecutionSection: MpmAssignmentSectionNodes;
  private readonly ohovExecutionToggle: MpmEnabledToggleNodes;
  private readonly ohovExecutionButton: MpmExecutionSlotNodes;
  private readonly mhovMultiplierInput: MpmTextInputNodes;
  private readonly mhovIntervalInput: MpmTextInputNodes;
  private readonly ohovHovCoreInput: MpmTextInputNodes;
  private readonly ohovIntervalInput: MpmTextInputNodes;
  private readonly amplifierPanelsAssignAllAction: MpmHeaderActionNodes;
  private readonly amplifierPanelsClearAllAction: MpmHeaderActionNodes;
  private readonly amplifierPanelsEnabledToggle: MpmEnabledToggleNodes;
  private readonly amplifierUpgradesSection: MpmAssignmentSectionNodes;
  private readonly amplifierUpgradesClearAllAction: MpmHeaderActionNodes;
  private readonly amplifierUpgradesEnabledToggle: MpmEnabledToggleNodes;
  private readonly explorationSection: MpmAssignmentSectionNodes;
  private readonly explorationAssignAllAction: MpmHeaderActionNodes;
  private readonly explorationClearAllAction: MpmHeaderActionNodes;
  private readonly explorationEnabledToggle: MpmEnabledToggleNodes;
  private readonly explorationBiomeGroups: readonly MpmExplorationBiomeNodes[];
  private readonly explorationSlots: readonly MpmExplorationSlotNodes[];
  private readonly hovTargetSlots: readonly MpmHovTargetSlotNodes[];
  private readonly hovGainUpgradeRow: HTMLDivElement;
  private readonly hovGainUpgradeSlots: readonly MpmHovGainUpgradeSlotNodes[];
  private readonly amplifierPanelRow: HTMLDivElement;
  private readonly amplifierPanelSlots: readonly MpmAmplifierPanelSlotNodes[];
  private readonly amplifierUpgradeRow: HTMLDivElement;
  private readonly amplifierUpgradeSlots: readonly MpmAmplifierUpgradeSlotNodes[];
  private readonly mpAvailabilityHud: HTMLDivElement;
  private readonly mpAvailabilityHudValue: HTMLSpanElement;

  constructor(options: MpmViewOptions) {
    const messages = getMessages();
    this.pointerModel = options.pointerModel;
    this.onMpAssign = options.onMpAssign;
    this.onMpRemove = options.onMpRemove;
    this.onHovGainUpgradeMpAssign = options.onHovGainUpgradeMpAssign;
    this.onHovGainUpgradeMpRemove = options.onHovGainUpgradeMpRemove;
    this.onAmplifierPanelMpAssign = options.onAmplifierPanelMpAssign;
    this.onAmplifierPanelMpRemove = options.onAmplifierPanelMpRemove;
    this.onAmplifierUpgradeMpAssign = options.onAmplifierUpgradeMpAssign;
    this.onAmplifierUpgradeMpRemove = options.onAmplifierUpgradeMpRemove;
    this.onExplorationMpAssign = options.onExplorationMpAssign;
    this.onExplorationMpRemove = options.onExplorationMpRemove;
    this.onMHovExecutionMpAssign = options.onMHovExecutionMpAssign;
    this.onMHovExecutionMpRemove = options.onMHovExecutionMpRemove;
    this.onNHovExecutionMpAssign = options.onNHovExecutionMpAssign;
    this.onNHovExecutionMpRemove = options.onNHovExecutionMpRemove;
    this.onOHovExecutionMpAssign = options.onOHovExecutionMpAssign;
    this.onOHovExecutionMpRemove = options.onOHovExecutionMpRemove;
    this.onMHovExecutionMultiplierSave = options.onMHovExecutionMultiplierSave;
    this.onMHovExecutionMinimumResetIntervalMsSave =
      options.onMHovExecutionMinimumResetIntervalMsSave;
    this.onNHovExecutionMinimumEffectSave =
      options.onNHovExecutionMinimumEffectSave;
    this.onNHovExecutionMinimumTimeSecondsSave =
      options.onNHovExecutionMinimumTimeSecondsSave;
    this.onOHovExecutionMinimumHovCoreSave =
      options.onOHovExecutionMinimumHovCoreSave;
    this.onOHovExecutionMinimumIntervalMsSave =
      options.onOHovExecutionMinimumIntervalMsSave;
    this.onMHovExecutionEnabledChange = options.onMHovExecutionEnabledChange;
    this.onNHovExecutionEnabledChange = options.onNHovExecutionEnabledChange;
    this.onOHovExecutionEnabledChange = options.onOHovExecutionEnabledChange;
    this.onHovTargetsClearAll = options.onHovTargetsClearAll;
    this.onHovGainUpgradesClearAll = options.onHovGainUpgradesClearAll;
    this.onAmplifierPanelsClearAll = options.onAmplifierPanelsClearAll;
    this.onAmplifierUpgradesClearAll = options.onAmplifierUpgradesClearAll;
    this.onExplorationClearAll = options.onExplorationClearAll;
    this.onHovTargetsAssignAll = options.onHovTargetsAssignAll;
    this.onHovGainUpgradesAssignAll = options.onHovGainUpgradesAssignAll;
    this.onAmplifierPanelsAssignAll = options.onAmplifierPanelsAssignAll;
    this.onExplorationAssignAll = options.onExplorationAssignAll;
    this.onHovTargetsEnabledChange = options.onHovTargetsEnabledChange;
    this.onHovGainUpgradesEnabledChange =
      options.onHovGainUpgradesEnabledChange;
    this.onAmplifierPanelsEnabledChange =
      options.onAmplifierPanelsEnabledChange;
    this.onAmplifierUpgradesEnabledChange =
      options.onAmplifierUpgradesEnabledChange;
    this.onExplorationEnabledChange = options.onExplorationEnabledChange;
    this.onPresetSave = options.onPresetSave;
    this.onPresetLoad = options.onPresetLoad;
    this.getMpmPresetSlotCount =
      options.getMpmPresetSlotCount ?? (() => MPM_PRESET_BASE_SLOT_COUNT);
    this.getHoverToggleDurationMs = options.getHoverToggleDurationMs ?? null;
    this.getHoverChargeDurationMs = options.getHoverChargeDurationMs;

    this.root = document.createElement("div");
    this.root.className = "hover-idle-mpm-panel";

    this.mpAvailabilityHud = document.createElement("div");
    this.mpAvailabilityHud.className =
      "hover-idle-hud-card hover-idle-mpm-mp-hud";
    this.mpAvailabilityHud.dataset.role = "mp-availability";
    this.mpAvailabilityHud.setAttribute("role", "status");
    this.mpAvailabilityHud.setAttribute("aria-live", "polite");
    this.mpAvailabilityHudValue = document.createElement("span");
    this.mpAvailabilityHudValue.className =
      "hover-idle-mpm-mp-hud-value hover-idle-hud-card__primary";
    this.mpAvailabilityHud.append(this.mpAvailabilityHudValue);
    this.applyMpAvailability(0, 0);

    const header = document.createElement("header");
    header.className = "hover-idle-mpm-header";

    const title = document.createElement("h1");
    title.textContent = messages.mpm.title;

    const subtitle = document.createElement("p");
    subtitle.className = "hover-idle-mpm-subtitle";
    subtitle.textContent = messages.mpm.subtitle;

    header.append(title, subtitle);

    const assignmentsSection = document.createElement("section");
    assignmentsSection.className = "hover-idle-mpm-section";
    assignmentsSection.dataset.group = "mp-assignments";

    const content = document.createElement("div");
    content.className = "hover-idle-mpm-content";

    const presetColumn = document.createElement("aside");
    presetColumn.className = "hover-idle-mpm-presets-column";
    presetColumn.dataset.group = "presets";

    const presetTitle = document.createElement("h2");
    presetTitle.className = "hover-idle-mpm-section-title";
    presetTitle.textContent = messages.mpm.presets.title;

    const presetList = document.createElement("div");
    presetList.className = "hover-idle-mpm-preset-list";
    this.presetSlots = Array.from(
      { length: MPM_PRESET_MAX_SLOT_COUNT },
      (_, presetIndex) => this.createPresetSlot(presetIndex),
    );
    presetList.append(...this.presetSlots.map((slot) => slot.element));
    presetColumn.append(presetTitle, presetList);

    this.hovTargetsContainer = document.createElement("div");
    this.hovTargetsContainer.className = "hover-idle-mpm-hov-targets-slot";
    this.hovTargetsContainer.dataset.slot = "hov-targets";

    const slotRows = document.createElement("div");
    slotRows.className =
      "hover-idle-mpm-slot-rows hover-idle-mpm-assignment-sections";

    const hovTargetsGrid = document.createElement("div");
    hovTargetsGrid.className =
      "hover-idle-mpm-slot-row hover-idle-mpm-hov-target-grid";
    hovTargetsGrid.dataset.row = "hov-targets";
    const hovTargetSpacer = document.createElement("div");
    hovTargetSpacer.className = "hover-idle-mpm-hov-target-spacer";
    hovTargetSpacer.dataset.slot = "hov-target-spacer";
    hovTargetSpacer.setAttribute("aria-hidden", "true");
    this.hovTargetSlots = Array.from(
      { length: MPM_HOV_TARGET_EXPANDED_SLOT_COUNT },
      (_, index) => this.createHovTargetSlot(index),
    );
    hovTargetsGrid.append(
      hovTargetSpacer,
      ...this.hovTargetSlots.map((slot) => slot.element),
    );

    this.hovTargetsAssignAllAction = this.createHeaderAction({
      section: "hov-targets",
      action: "assign-all",
      variant: "positive",
      label: messages.mpm.actions.assignAll,
      ariaLabel: messages.mpm.assignAllAriaLabel(messages.mpm.sections.targets),
      onExecute: () => this.onHovTargetsAssignAll(),
    });
    this.hovTargetsAssignAllAction.element.hidden = true;

    this.hovTargetsClearAllAction = this.createHeaderAction({
      section: "hov-targets",
      label: messages.mpm.actions.clearAll,
      ariaLabel: messages.mpm.clearAllAriaLabel(messages.mpm.sections.targets),
      onExecute: () => this.onHovTargetsClearAll(),
    });

    this.hovTargetsEnabledToggle = this.createEnabledToggle({
      section: "hov-targets",
      onEnabledChange: (enabled) => this.onHovTargetsEnabledChange(enabled),
    });

    this.hovTargetsSection = this.createAssignmentSection({
      section: "hov-targets",
      title: messages.mpm.sections.targets,
      actions: [
        this.hovTargetsAssignAllAction.element,
        this.hovTargetsClearAllAction.element,
        this.hovTargetsEnabledToggle.element,
      ],
      row: hovTargetsGrid,
    });

    this.hovGainUpgradeRow = document.createElement("div");
    this.hovGainUpgradeRow.className =
      "hover-idle-mpm-slot-row hover-idle-mpm-hov-gain-upgrade-row";
    this.hovGainUpgradeRow.dataset.row = "hov-gain-upgrades";
    this.hovGainUpgradeRow.hidden = true;
    this.hovGainUpgradeSlots = Array.from(
      { length: MPM_HOV_TARGET_EXPANDED_SLOT_COUNT + 1 },
      (_, index) => this.createHovGainUpgradeSlot(index),
    );
    this.hovGainUpgradeRow.append(
      ...this.hovGainUpgradeSlots.map((slot) => slot.element),
    );

    this.hovGainUpgradesAssignAllAction = this.createHeaderAction({
      section: "hov-gain-upgrades",
      action: "assign-all",
      variant: "positive",
      label: messages.mpm.actions.assignAll,
      ariaLabel: messages.mpm.assignAllAriaLabel(
        messages.mpm.sections.upgrades,
      ),
      onExecute: () => this.onHovGainUpgradesAssignAll(),
    });
    this.hovGainUpgradesAssignAllAction.element.hidden = true;

    this.hovGainUpgradesClearAllAction = this.createHeaderAction({
      section: "hov-gain-upgrades",
      label: messages.mpm.actions.clearAll,
      ariaLabel: messages.mpm.clearAllAriaLabel(messages.mpm.sections.upgrades),
      onExecute: () => this.onHovGainUpgradesClearAll(),
    });

    this.hovGainUpgradesEnabledToggle = this.createEnabledToggle({
      section: "hov-gain-upgrades",
      onEnabledChange: (enabled) =>
        this.onHovGainUpgradesEnabledChange(enabled),
    });

    this.hovGainUpgradesSection = this.createAssignmentSection({
      section: "hov-gain-upgrades",
      title: messages.mpm.sections.upgrades,
      actions: [
        this.hovGainUpgradesAssignAllAction.element,
        this.hovGainUpgradesClearAllAction.element,
        this.hovGainUpgradesEnabledToggle.element,
      ],
      row: this.hovGainUpgradeRow,
    });
    this.hovGainUpgradesSection.element.hidden = true;

    const mhovExecutionRow = document.createElement("div");
    mhovExecutionRow.className =
      "hover-idle-mpm-reset-condition-row hover-idle-mpm-mhov-execution-row";
    mhovExecutionRow.dataset.row = "mhov-execution";
    this.mhovExecutionButton = this.createMHovExecutionSlot();
    this.mhovMultiplierInput = this.createMpmExecutionInput({
      label: messages.mpm.mhovExecutionInputs.multiplier,
      saveAction: "mhov-execution-input-save",
      onSave: (value) => this.onMHovExecutionMultiplierSave(value),
    });
    this.mhovIntervalInput = this.createMpmExecutionInput({
      label: messages.mpm.mhovExecutionInputs.minimumResetIntervalSeconds,
      saveAction: "mhov-execution-input-save",
      onSave: (value) =>
        this.onMHovExecutionMinimumResetIntervalMsSave(
          Number(value) * MPM_MHOV_EXECUTION_INTERVAL_MS_PER_SECOND,
        ),
    });
    const mhovAssignmentRow = this.createResetAssignmentCell({
      className: "hover-idle-mpm-mhov-execution-control-row",
      slot: this.mhovExecutionButton.element,
    });
    mhovAssignmentRow.dataset.mhovExecutionRow = "assignment-control";
    this.mhovMultiplierInput.root.dataset.mhovExecutionRow = "multiplier";
    this.mhovIntervalInput.root.dataset.mhovExecutionRow =
      "minimum-reset-interval-ms";
    mhovExecutionRow.append(
      mhovAssignmentRow,
      this.mhovMultiplierInput.root,
      this.mhovIntervalInput.root,
    );
    this.mhovExecutionToggle = this.createEnabledToggle({
      section: "mhov-execution",
      onEnabledChange: (enabled) => this.onMHovExecutionEnabledChange(enabled),
    });
    this.mhovExecutionSection = this.createAssignmentSection({
      section: "mhov-execution",
      title: messages.mpm.sections.mhovExecution,
      actions: [this.mhovExecutionToggle.element],
      row: mhovExecutionRow,
    });
    this.mhovExecutionSection.element.hidden = true;

    const nhovExecutionRow = document.createElement("div");
    nhovExecutionRow.className = "hover-idle-mpm-nhov-execution-row";
    nhovExecutionRow.dataset.row = "nhov-execution";
    const nhovExecutionSlots: MpmNHovExecutionSlotNodes[] = [];
    const nhovConditionInputEntries: [
      NHovBranchId,
      MpmExecutionConditionInputNodes,
    ][] = [];
    for (const branchId of NHOV_BRANCH_IDS) {
      const branchRow = this.createNHovExecutionBranchRow(branchId, messages);
      nhovExecutionSlots.push(branchRow.slot);
      nhovConditionInputEntries.push([branchId, branchRow.inputs]);
      nhovExecutionRow.append(branchRow.element);
    }
    this.nhovExecutionSlots = nhovExecutionSlots;
    this.nhovConditionInputs = Object.fromEntries(
      nhovConditionInputEntries,
    ) as Record<NHovBranchId, MpmExecutionConditionInputNodes>;
    this.nhovExecutionToggle = this.createEnabledToggle({
      section: "nhov-execution",
      onEnabledChange: (enabled) => this.onNHovExecutionEnabledChange(enabled),
    });
    this.nhovExecutionSection = this.createAssignmentSection({
      section: "nhov-execution",
      title: messages.mpm.sections.nhovExecution,
      actions: [this.nhovExecutionToggle.element],
      row: nhovExecutionRow,
    });
    this.nhovExecutionSection.element.hidden = true;

    const ohovExecutionRow = document.createElement("div");
    ohovExecutionRow.className =
      "hover-idle-mpm-reset-condition-row hover-idle-mpm-ohov-execution-row";
    ohovExecutionRow.dataset.row = "ohov-execution";
    this.ohovExecutionButton = this.createOHovExecutionSlot();
    this.ohovHovCoreInput = this.createMpmExecutionInput({
      label: messages.mpm.ohovExecutionInputs.minimumHovCore,
      saveAction: "ohov-execution-input-save",
      onSave: (value) => this.onOHovExecutionMinimumHovCoreSave(value),
    });
    this.ohovIntervalInput = this.createMpmExecutionInput({
      label: messages.mpm.ohovExecutionInputs.minimumResetIntervalSeconds,
      saveAction: "ohov-execution-input-save",
      onSave: (value) =>
        this.onOHovExecutionMinimumIntervalMsSave(
          Number(value) * MPM_MHOV_EXECUTION_INTERVAL_MS_PER_SECOND,
        ),
    });
    const ohovAssignmentRow = this.createResetAssignmentCell({
      className: "hover-idle-mpm-ohov-execution-control-row",
      slot: this.ohovExecutionButton.element,
    });
    ohovAssignmentRow.dataset.ohovExecutionRow = "assignment-control";
    this.ohovHovCoreInput.root.dataset.ohovExecutionRow = "minimum-hov-core";
    this.ohovIntervalInput.root.dataset.ohovExecutionRow =
      "minimum-interval-ms";
    ohovExecutionRow.append(
      ohovAssignmentRow,
      this.ohovHovCoreInput.root,
      this.ohovIntervalInput.root,
    );
    this.ohovExecutionToggle = this.createEnabledToggle({
      section: "ohov-execution",
      onEnabledChange: (enabled) => this.onOHovExecutionEnabledChange(enabled),
    });
    this.ohovExecutionSection = this.createAssignmentSection({
      section: "ohov-execution",
      title: messages.mpm.sections.ohovExecution,
      actions: [this.ohovExecutionToggle.element],
      row: ohovExecutionRow,
    });
    this.ohovExecutionSection.element.hidden = true;

    this.amplifierPanelRow = document.createElement("div");
    this.amplifierPanelRow.className =
      "hover-idle-mpm-slot-row hover-idle-mpm-amplifier-panel-row";
    this.amplifierPanelRow.dataset.row = "amplifier-panels";
    this.amplifierPanelRow.hidden = true;
    this.amplifierPanelSlots = HOV_AMPLIFIER_ENTRY_DEFINITIONS.map(
      (definition) => this.createAmplifierPanelSlot(definition.id),
    );
    const amplifierPanelSpacer = document.createElement("div");
    amplifierPanelSpacer.className = "hover-idle-mpm-hov-target-spacer";
    amplifierPanelSpacer.dataset.slot = "amplifier-panel-spacer";
    amplifierPanelSpacer.setAttribute("aria-hidden", "true");
    this.amplifierPanelRow.append(
      amplifierPanelSpacer,
      ...this.amplifierPanelSlots.map((slot) => slot.element),
    );

    this.amplifierPanelsAssignAllAction = this.createHeaderAction({
      section: "amplifier-panels",
      action: "assign-all",
      variant: "positive",
      label: messages.mpm.actions.assignAll,
      ariaLabel: messages.mpm.assignAllAriaLabel(
        messages.mpm.sections.amplifierPanels,
      ),
      onExecute: () => this.onAmplifierPanelsAssignAll(),
    });
    this.amplifierPanelsAssignAllAction.element.hidden = true;

    this.amplifierPanelsClearAllAction = this.createHeaderAction({
      section: "amplifier-panels",
      label: messages.mpm.actions.clearAll,
      ariaLabel: messages.mpm.clearAllAriaLabel(
        messages.mpm.sections.amplifierPanels,
      ),
      onExecute: () => this.onAmplifierPanelsClearAll(),
    });

    this.amplifierPanelsEnabledToggle = this.createEnabledToggle({
      section: "amplifier-panels",
      onEnabledChange: (enabled) =>
        this.onAmplifierPanelsEnabledChange(enabled),
    });

    this.amplifierPanelsSection = this.createAssignmentSection({
      section: "amplifier-panels",
      title: messages.mpm.sections.amplifierPanels,
      actions: [
        this.amplifierPanelsAssignAllAction.element,
        this.amplifierPanelsClearAllAction.element,
        this.amplifierPanelsEnabledToggle.element,
      ],
      row: this.amplifierPanelRow,
    });
    this.amplifierPanelsSection.element.hidden = true;

    this.amplifierUpgradeRow = document.createElement("div");
    this.amplifierUpgradeRow.className =
      "hover-idle-mpm-slot-row hover-idle-mpm-amplifier-upgrade-row";
    this.amplifierUpgradeRow.dataset.row = "amplifier-upgrades";
    this.amplifierUpgradeRow.hidden = true;
    this.amplifierUpgradeSlots = HOV_AMPLIFIER_ENTRY_DEFINITIONS.map(
      (definition) => this.createAmplifierUpgradeSlot(definition.id),
    );
    const amplifierUpgradeSpacer = document.createElement("div");
    amplifierUpgradeSpacer.className = "hover-idle-mpm-hov-target-spacer";
    amplifierUpgradeSpacer.dataset.slot = "amplifier-upgrade-spacer";
    amplifierUpgradeSpacer.setAttribute("aria-hidden", "true");
    this.amplifierUpgradeRow.append(
      amplifierUpgradeSpacer,
      ...this.amplifierUpgradeSlots.map((slot) => slot.element),
    );

    this.amplifierUpgradesClearAllAction = this.createHeaderAction({
      section: "amplifier-upgrades",
      label: messages.mpm.actions.clearAll,
      ariaLabel: messages.mpm.clearAllAriaLabel(
        messages.mpm.sections.amplifierUpgrades,
      ),
      onExecute: () => this.onAmplifierUpgradesClearAll(),
    });

    this.amplifierUpgradesEnabledToggle = this.createEnabledToggle({
      section: "amplifier-upgrades",
      onEnabledChange: (enabled) =>
        this.onAmplifierUpgradesEnabledChange(enabled),
    });

    this.amplifierUpgradesSection = this.createAssignmentSection({
      section: "amplifier-upgrades",
      title: messages.mpm.sections.amplifierUpgrades,
      actions: [
        this.amplifierUpgradesClearAllAction.element,
        this.amplifierUpgradesEnabledToggle.element,
      ],
      row: this.amplifierUpgradeRow,
    });
    this.amplifierUpgradesSection.element.hidden = true;

    const explorationRow = document.createElement("div");
    explorationRow.className = "hover-idle-mpm-exploration-row";
    explorationRow.dataset.row = "exploration";
    this.explorationSlots = EXPLORATION_MP_SLOT_SEQUENCE.map((slot) =>
      this.createExplorationSlot(slot),
    );
    this.explorationBiomeGroups = EXPLORATION_BIOME_IDS.map((biomeId) => {
      const group = document.createElement("div");
      group.className =
        "hover-idle-mpm-reset-assignment-cell hover-idle-mpm-exploration-biome";
      group.dataset.explorationBiome = biomeId;
      group.hidden = true;

      const label = document.createElement("div");
      label.className =
        "hover-idle-mpm-reset-assignment-label hover-idle-mpm-exploration-assignment-label";
      label.textContent = messages.exploration.biomes[biomeId].name;

      const slots = document.createElement("div");
      slots.className = "hover-idle-mpm-exploration-biome-slots";
      slots.append(
        ...this.explorationSlots
          .filter((slot) => slot.biomeId === biomeId)
          .map((slot) => slot.element),
      );
      group.append(label, slots);
      explorationRow.append(group);
      return { biomeId, element: group };
    });

    this.explorationAssignAllAction = this.createHeaderAction({
      section: "exploration",
      action: "assign-all",
      variant: "positive",
      label: messages.mpm.actions.assignAll,
      ariaLabel: messages.mpm.assignAllAriaLabel(
        messages.mpm.sections.exploration,
      ),
      onExecute: () => this.onExplorationAssignAll(),
    });
    this.explorationAssignAllAction.element.hidden = true;
    this.explorationClearAllAction = this.createHeaderAction({
      section: "exploration",
      label: messages.mpm.actions.clearAll,
      ariaLabel: messages.mpm.clearAllAriaLabel(
        messages.mpm.sections.exploration,
      ),
      onExecute: () => this.onExplorationClearAll(),
    });
    this.explorationEnabledToggle = this.createEnabledToggle({
      section: "exploration",
      onEnabledChange: (enabled) => this.onExplorationEnabledChange(enabled),
    });
    this.explorationSection = this.createAssignmentSection({
      section: "exploration",
      title: messages.mpm.sections.exploration,
      actions: [
        this.explorationAssignAllAction.element,
        this.explorationClearAllAction.element,
        this.explorationEnabledToggle.element,
      ],
      row: explorationRow,
    });
    this.explorationSection.element.hidden = true;

    slotRows.append(
      this.hovTargetsSection.element,
      this.hovGainUpgradesSection.element,
      this.mhovExecutionSection.element,
      this.nhovExecutionSection.element,
      this.ohovExecutionSection.element,
      this.amplifierPanelsSection.element,
      this.amplifierUpgradesSection.element,
      this.explorationSection.element,
    );
    this.hovTargetsContainer.append(slotRows);

    assignmentsSection.append(this.hovTargetsContainer);
    content.append(presetColumn, assignmentsSection);
    this.root.append(header, content);
    options.container.replaceChildren(this.root, this.mpAvailabilityHud);
  }

  update(
    snapshot: Pick<
      HoverIdleSnapshot,
      | "mpCapacity"
      | "mpm"
      | "exploration"
      | "hovAmplifier"
      | "hovTargetDirectMpBlockedByActiveWall"
      | "hovTargetDirectMpBlockedReason"
      | "hovGainUpgradeDirectMpBlockedByActiveWall"
      | "hovGainUpgradeDirectMpBlockedReason"
    >,
  ): void {
    const messages = getMessages();
    const capacity = Math.max(0, Math.trunc(snapshot.mpCapacity));
    const available = computeMpmAvailableMpFromOccupancy({
      mpCapacity: capacity,
      state: snapshot.mpm,
    });
    this.applyMpAvailability(available, capacity);
    this.applyPresetSlots(snapshot.mpm.presets);

    this.hovTargetsContainer.dataset.expanded =
      resolveMpmHovTargetSlotCount(snapshot.mpm.gridUnlock.hov) ===
      MPM_HOV_TARGET_EXPANDED_SLOT_COUNT
        ? "true"
        : "false";
    this.hovTargetsClearAllAction.controller.sync({
      disabled: snapshot.mpm.hovTargets.assignments.length === 0,
    });
    const hovTargetsAssignAllState = resolveMpmHovTargetsAssignAllState({
      mpCapacity: snapshot.mpCapacity,
      mpm: snapshot.mpm,
    });
    const hovTargetsAssignAllVisible = hovTargetsAssignAllState.shouldShow;
    this.hovTargetsAssignAllAction.element.hidden = !hovTargetsAssignAllVisible;
    this.hovTargetsAssignAllAction.controller.sync({
      disabled:
        !hovTargetsAssignAllVisible ||
        !snapshot.mpm.hovTargets.enabled ||
        !hovTargetsAssignAllState.canExecute,
    });
    if (!hovTargetsAssignAllVisible) {
      this.hovTargetsAssignAllAction.controller.clearPointerHover();
    }
    this.applySectionEnabledState({
      section: this.hovTargetsSection,
      toggle: this.hovTargetsEnabledToggle,
      title: messages.mpm.sections.targets,
      enabled: snapshot.mpm.hovTargets.enabled,
    });

    const slots = createMpmHovTargetSlots(snapshot);
    const slotsByIndex = new Map(slots.map((slot) => [slot.index, slot]));

    for (const nodes of this.hovTargetSlots) {
      const slot = slotsByIndex.get(nodes.index);
      const visible = slot !== undefined;
      nodes.element.hidden = !visible;
      if (!visible) {
        nodes.controller.clearPointerHover();
        this.applySlotState(nodes, false, false);
        continue;
      }

      const directMpState = resolveDirectMpTargetViewState({
        assigned: slot.assigned,
        targetActive: true,
        sectionEnabled: snapshot.mpm.hovTargets.enabled,
        canAssign: !slot.disabled,
        directMpBlockedByActiveWall:
          snapshot.hovTargetDirectMpBlockedByActiveWall,
        directMpBlockedReason: snapshot.hovTargetDirectMpBlockedReason,
      });
      this.applySlotState(nodes, slot.assigned, slot.disabled, {
        operative: directMpState.operative,
      });
      nodes.element.setAttribute(
        "aria-label",
        `${slot.label} MP ${slot.assigned ? "assigned" : "unassigned"}`,
      );
    }
    this.applySectionMpBlockedState(
      this.hovTargetsSection,
      snapshot.hovTargetDirectMpBlockedByActiveWall,
      snapshot.hovTargetDirectMpBlockedReason,
    );

    const upgradeSlots = createMpmHovGainUpgradeSlots(snapshot);
    const upgradeSlotsByIndex = new Map(
      upgradeSlots.map((slot) => [slot.index, slot]),
    );
    const hasUpgradeAssignments =
      snapshot.mpm.hovGainUpgrades.assignments.length > 0;
    this.hovGainUpgradesSection.element.hidden =
      upgradeSlots.length === 0 && !hasUpgradeAssignments;
    this.hovGainUpgradeRow.hidden = upgradeSlots.length === 0;
    this.hovGainUpgradesClearAllAction.controller.sync({
      disabled: !hasUpgradeAssignments,
    });
    const hovGainUpgradesAssignAllState =
      resolveMpmHovGainUpgradesAssignAllState({
        mpCapacity: snapshot.mpCapacity,
        mpm: snapshot.mpm,
      });
    const hovGainUpgradesAssignAllVisible =
      hovGainUpgradesAssignAllState.shouldShow;
    this.hovGainUpgradesAssignAllAction.element.hidden =
      !hovGainUpgradesAssignAllVisible;
    this.hovGainUpgradesAssignAllAction.controller.sync({
      disabled:
        !hovGainUpgradesAssignAllVisible ||
        !snapshot.mpm.hovGainUpgrades.enabled ||
        !hovGainUpgradesAssignAllState.canExecute,
    });
    this.applySectionEnabledState({
      section: this.hovGainUpgradesSection,
      toggle: this.hovGainUpgradesEnabledToggle,
      title: messages.mpm.sections.upgrades,
      enabled: snapshot.mpm.hovGainUpgrades.enabled,
    });
    if (
      this.hovGainUpgradesSection.element.hidden ||
      !hovGainUpgradesAssignAllVisible
    ) {
      this.hovGainUpgradesAssignAllAction.controller.clearPointerHover();
    }
    if (this.hovGainUpgradesSection.element.hidden) {
      this.hovGainUpgradesClearAllAction.controller.clearPointerHover();
      this.hovGainUpgradesEnabledToggle.clearPointerHover();
    }
    for (const nodes of this.hovGainUpgradeSlots) {
      const slot = upgradeSlotsByIndex.get(nodes.index);
      const visible = slot !== undefined;
      nodes.element.hidden = !visible;
      if (!visible) {
        nodes.controller.clearPointerHover();
        nodes.target = null;
        this.applySlotState(nodes, false, false);
        continue;
      }

      nodes.target = slot.target;
      const directMpState = resolveDirectMpTargetViewState({
        assigned: slot.assigned,
        targetActive: true,
        sectionEnabled: snapshot.mpm.hovGainUpgrades.enabled,
        canAssign: !slot.disabled,
        directMpBlockedByActiveWall:
          snapshot.hovGainUpgradeDirectMpBlockedByActiveWall,
        directMpBlockedReason: snapshot.hovGainUpgradeDirectMpBlockedReason,
      });
      this.applySlotState(nodes, slot.assigned, slot.disabled, {
        operative: directMpState.operative,
      });
      nodes.element.setAttribute(
        "aria-label",
        `${slot.label} MP ${slot.assigned ? "assigned" : "unassigned"}`,
      );
    }
    this.applySectionMpBlockedState(
      this.hovGainUpgradesSection,
      snapshot.hovGainUpgradeDirectMpBlockedByActiveWall,
      snapshot.hovGainUpgradeDirectMpBlockedReason,
    );

    const mhov = snapshot.mpm.mhovExecution;
    this.mhovExecutionSection.element.hidden = !mhov.unlocked && !mhov.assigned;
    this.applySectionEnabledState({
      section: this.mhovExecutionSection,
      toggle: this.mhovExecutionToggle,
      title: messages.mpm.sections.mhovExecution,
      enabled: mhov.enabled,
    });
    this.applySlotState(
      this.mhovExecutionButton,
      mhov.assigned,
      !mhov.unlocked ||
        (!mhov.assigned && available < MPM_MHOV_EXECUTION_MP_COST),
    );
    this.applyMissingMpTooltip(this.mhovExecutionButton, {
      requiredMp: MPM_MHOV_EXECUTION_MP_COST,
      availableMp: available,
      shouldShow: mhov.unlocked && !mhov.assigned,
    });
    this.applyTextInputSavedValue(
      this.mhovMultiplierInput,
      mhov.multiplier.toString(),
    );
    this.applyTextInputSavedValue(
      this.mhovIntervalInput,
      this.formatMHovExecutionIntervalSeconds(mhov.minimumResetIntervalMs),
    );
    if (this.mhovExecutionSection.element.hidden) {
      this.mhovExecutionToggle.clearPointerHover();
      this.mhovExecutionButton.controller.clearPointerHover();
    }

    const nhov = snapshot.mpm.nhovExecution;
    this.nhovExecutionSection.element.hidden =
      !nhov.unlocked && nhov.assignments.length === 0;
    this.applySectionEnabledState({
      section: this.nhovExecutionSection,
      toggle: this.nhovExecutionToggle,
      title: messages.mpm.sections.nhovExecution,
      enabled: nhov.enabled,
    });
    for (const nodes of this.nhovExecutionSlots) {
      const assigned = nhov.assignments.includes(nodes.branchId);
      this.applySlotState(
        nodes,
        assigned,
        !assigned &&
          (!nhov.unlocked || available < MPM_NHOV_EXECUTION_MP_COST_PER_BRANCH),
      );
      this.applyMissingMpTooltip(nodes, {
        requiredMp: MPM_NHOV_EXECUTION_MP_COST_PER_BRANCH,
        availableMp: available,
        shouldShow: nhov.unlocked && !assigned,
      });
      const inputs = this.nhovConditionInputs[nodes.branchId];
      const condition = nhov.conditions[nodes.branchId];
      this.applyTextInputSavedValue(
        inputs.effect,
        condition.minimumEffect.toString(),
      );
      this.applyTextInputSavedValue(
        inputs.interval,
        String(condition.minimumTimeSeconds),
      );
    }
    if (this.nhovExecutionSection.element.hidden) {
      this.nhovExecutionToggle.clearPointerHover();
      for (const nodes of this.nhovExecutionSlots) {
        nodes.controller.clearPointerHover();
      }
    }

    const ohov = snapshot.mpm.ohovExecution;
    this.ohovExecutionSection.element.hidden = !ohov.unlocked && !ohov.assigned;
    this.applySectionEnabledState({
      section: this.ohovExecutionSection,
      toggle: this.ohovExecutionToggle,
      title: messages.mpm.sections.ohovExecution,
      enabled: ohov.enabled,
    });
    this.applySlotState(
      this.ohovExecutionButton,
      ohov.assigned,
      !ohov.assigned &&
        (!ohov.unlocked || available < MPM_OHOV_EXECUTION_MP_COST),
    );
    this.applyMissingMpTooltip(this.ohovExecutionButton, {
      requiredMp: MPM_OHOV_EXECUTION_MP_COST,
      availableMp: available,
      shouldShow: ohov.unlocked && !ohov.assigned,
    });
    this.applyTextInputSavedValue(
      this.ohovHovCoreInput,
      ohov.minimumHovCore.toString(),
    );
    this.applyTextInputSavedValue(
      this.ohovIntervalInput,
      this.formatMHovExecutionIntervalSeconds(ohov.minimumIntervalMs),
    );
    if (this.ohovExecutionSection.element.hidden) {
      this.ohovExecutionToggle.clearPointerHover();
      this.ohovExecutionButton.controller.clearPointerHover();
    }

    const ampPanelSlots = createMpmAmplifierPanelSlots(snapshot);
    const ampPanelSlotsByEntryId = new Map(
      ampPanelSlots.map((slot) => [slot.entryId, slot]),
    );
    const hasAmpPanelAssignments =
      snapshot.mpm.amplifierPanels.assignments.length > 0;
    this.amplifierPanelsSection.element.hidden =
      ampPanelSlots.length === 0 && !hasAmpPanelAssignments;
    this.amplifierPanelRow.hidden = ampPanelSlots.length === 0;
    this.amplifierPanelsClearAllAction.controller.sync({
      disabled: !hasAmpPanelAssignments,
    });
    const assignAllState = resolveMpmAmplifierPanelAssignAllState(snapshot);
    const assignAllVisible = assignAllState.shouldShow;
    this.amplifierPanelsAssignAllAction.element.hidden = !assignAllVisible;
    this.amplifierPanelsAssignAllAction.controller.sync({
      disabled:
        !assignAllVisible ||
        !snapshot.mpm.amplifierPanels.enabled ||
        !assignAllState.canExecute,
    });
    this.applySectionEnabledState({
      section: this.amplifierPanelsSection,
      toggle: this.amplifierPanelsEnabledToggle,
      title: messages.mpm.sections.amplifierPanels,
      enabled: snapshot.mpm.amplifierPanels.enabled,
    });
    if (this.amplifierPanelsSection.element.hidden || !assignAllVisible) {
      this.amplifierPanelsAssignAllAction.controller.clearPointerHover();
    }
    if (this.amplifierPanelsSection.element.hidden) {
      this.amplifierPanelsClearAllAction.controller.clearPointerHover();
      this.amplifierPanelsEnabledToggle.clearPointerHover();
    }
    for (const nodes of this.amplifierPanelSlots) {
      const slot = ampPanelSlotsByEntryId.get(nodes.entryId);
      const visible = slot !== undefined;
      nodes.element.hidden = !visible;
      if (!visible) {
        nodes.controller.clearPointerHover();
        this.applySlotState(nodes, false, false);
        continue;
      }

      this.applySlotState(nodes, slot.assigned, slot.disabled);
      nodes.element.setAttribute(
        "aria-label",
        `${slot.label} MP ${slot.assigned ? "assigned" : "unassigned"}`,
      );
    }

    const ampUpgradeSlots = createMpmAmplifierUpgradeSlots(snapshot);
    const ampUpgradeSlotsByEntryId = new Map(
      ampUpgradeSlots.map((slot) => [slot.entryId, slot]),
    );
    const hasAmpUpgradeAssignments =
      snapshot.mpm.amplifierUpgrades.assignments.length > 0;
    this.amplifierUpgradesSection.element.hidden =
      ampUpgradeSlots.length === 0 && !hasAmpUpgradeAssignments;
    this.amplifierUpgradeRow.hidden = ampUpgradeSlots.length === 0;
    this.amplifierUpgradesClearAllAction.controller.sync({
      disabled: !hasAmpUpgradeAssignments,
    });
    this.applySectionEnabledState({
      section: this.amplifierUpgradesSection,
      toggle: this.amplifierUpgradesEnabledToggle,
      title: messages.mpm.sections.amplifierUpgrades,
      enabled: snapshot.mpm.amplifierUpgrades.enabled,
    });
    if (this.amplifierUpgradesSection.element.hidden) {
      this.amplifierUpgradesClearAllAction.controller.clearPointerHover();
      this.amplifierUpgradesEnabledToggle.clearPointerHover();
    }
    for (const nodes of this.amplifierUpgradeSlots) {
      const slot = ampUpgradeSlotsByEntryId.get(nodes.entryId);
      const visible = slot !== undefined;
      nodes.element.hidden = !visible;
      if (!visible) {
        nodes.controller.clearPointerHover();
        this.applySlotState(nodes, false, false);
        continue;
      }

      this.applySlotState(nodes, slot.assigned, slot.disabled);
      nodes.element.setAttribute(
        "aria-label",
        `${slot.label} MP ${slot.assigned ? "assigned" : "unassigned"}`,
      );
    }

    const purchasedExplorationSlots = snapshot.exploration.purchasedMpSlots;
    const purchasedExplorationSlotIds = new Set(
      purchasedExplorationSlots.map((slot) => slot.id),
    );
    const assignedExplorationSlotIds = new Set(
      snapshot.mpm.exploration.assignedSlotIds,
    );
    const explorationEnabled = snapshot.mpm.exploration.enabled;
    this.explorationSection.element.hidden =
      purchasedExplorationSlots.length === 0;
    this.explorationClearAllAction.controller.sync({
      disabled: assignedExplorationSlotIds.size === 0,
    });
    const explorationAssignAllState = resolveMpmExplorationAssignAllState({
      mpCapacity: snapshot.mpCapacity,
      mpm: snapshot.mpm,
      purchasedMpSlots: purchasedExplorationSlots,
    });
    this.explorationAssignAllAction.element.hidden =
      !explorationAssignAllState.shouldShow;
    this.explorationAssignAllAction.controller.sync({
      disabled:
        !explorationAssignAllState.shouldShow ||
        !explorationEnabled ||
        !explorationAssignAllState.canExecute,
    });
    if (!explorationAssignAllState.shouldShow) {
      this.explorationAssignAllAction.controller.clearPointerHover();
    }
    this.applySectionEnabledState({
      section: this.explorationSection,
      toggle: this.explorationEnabledToggle,
      title: messages.mpm.sections.exploration,
      enabled: explorationEnabled,
    });
    for (const group of this.explorationBiomeGroups) {
      group.element.hidden = !purchasedExplorationSlots.some(
        (slot) => slot.biomeId === group.biomeId,
      );
    }
    for (const nodes of this.explorationSlots) {
      const visible = purchasedExplorationSlotIds.has(nodes.slotId);
      nodes.element.hidden = !visible;
      if (!visible) {
        nodes.controller.clearPointerHover();
        this.applySlotState(nodes, false, false);
        this.applyMissingMpTooltip(nodes, {
          requiredMp: nodes.requiredMp,
          availableMp: available,
          shouldShow: false,
        });
        continue;
      }
      const assigned = assignedExplorationSlotIds.has(nodes.slotId);
      const insufficientMp = !assigned && available < nodes.requiredMp;
      const disabled = !assigned && (!explorationEnabled || insufficientMp);
      this.applySlotState(nodes, assigned, disabled, {
        operative: assigned && explorationEnabled,
      });
      nodes.element.dataset.availability = assigned
        ? "assigned"
        : !explorationEnabled
          ? "blocked"
          : insufficientMp
            ? "insufficient-mp"
            : "available";
      if (!assigned && !explorationEnabled) {
        nodes.element.dataset.blockedReason = "mpm-section-disabled";
      } else if (insufficientMp) {
        nodes.element.dataset.blockedReason = "global-mp-capacity-exceeded";
      } else {
        delete nodes.element.dataset.blockedReason;
      }
      this.applyMissingMpTooltip(nodes, {
        requiredMp: nodes.requiredMp,
        availableMp: available,
        shouldShow: explorationEnabled && !assigned,
      });
      const amount = String(nodes.requiredMp);
      nodes.element.setAttribute(
        "aria-label",
        assigned
          ? messages.exploration.removeMpSlotAria(amount)
          : messages.exploration.assignMpSlotAria(amount),
      );
    }
    if (this.explorationSection.element.hidden) {
      this.explorationAssignAllAction.controller.clearPointerHover();
      this.explorationClearAllAction.controller.clearPointerHover();
      this.explorationEnabledToggle.clearPointerHover();
    }
  }

  dispose(): void {
    for (const nodes of this.presetSlots) {
      nodes.saveAction.controller.dispose();
      nodes.loadAction.controller.dispose();
    }
    this.hovTargetsAssignAllAction.controller.dispose();
    this.hovTargetsClearAllAction.controller.dispose();
    this.hovTargetsEnabledToggle.dispose();
    this.hovGainUpgradesAssignAllAction.controller.dispose();
    this.hovGainUpgradesClearAllAction.controller.dispose();
    this.hovGainUpgradesEnabledToggle.dispose();
    this.mhovExecutionToggle.dispose();
    this.mhovExecutionButton.controller.dispose();
    this.mhovExecutionButton.missingMpTooltip.cleanup();
    this.nhovExecutionToggle.dispose();
    for (const nodes of this.nhovExecutionSlots) {
      nodes.controller.dispose();
      nodes.missingMpTooltip.cleanup();
    }
    this.ohovExecutionToggle.dispose();
    this.ohovExecutionButton.controller.dispose();
    this.ohovExecutionButton.missingMpTooltip.cleanup();
    this.amplifierPanelsAssignAllAction.controller.dispose();
    this.amplifierPanelsClearAllAction.controller.dispose();
    this.amplifierPanelsEnabledToggle.dispose();
    this.amplifierUpgradesClearAllAction.controller.dispose();
    this.amplifierUpgradesEnabledToggle.dispose();
    this.explorationAssignAllAction.controller.dispose();
    this.explorationClearAllAction.controller.dispose();
    this.explorationEnabledToggle.dispose();
    for (const nodes of this.explorationSlots) {
      nodes.controller.dispose();
      nodes.missingMpTooltip.cleanup();
    }
    for (const nodes of this.hovTargetSlots) {
      nodes.controller.dispose();
    }
    for (const nodes of this.hovGainUpgradeSlots) {
      nodes.controller.dispose();
    }
    for (const nodes of this.amplifierPanelSlots) {
      nodes.controller.dispose();
    }
    for (const nodes of this.amplifierUpgradeSlots) {
      nodes.controller.dispose();
    }
    this.mpAvailabilityHud.remove();
    this.root.remove();
  }

  private createAssignmentSection(options: {
    readonly section: MpmAssignmentSectionId;
    readonly title: string;
    readonly actions: readonly HTMLButtonElement[];
    readonly row: HTMLDivElement;
  }): MpmAssignmentSectionNodes {
    const section = document.createElement("div");
    section.className = "hover-idle-mpm-assignment-section";
    section.dataset.section = options.section;
    section.dataset.enabled = "true";

    const header = document.createElement("div");
    header.className = "hover-idle-mpm-section-header";

    const title = document.createElement("h2");
    title.className = "hover-idle-mpm-section-title";
    title.textContent = options.title;

    const actions = document.createElement("div");
    actions.className = "hover-idle-mpm-section-actions";
    actions.append(...options.actions);

    header.append(title, actions);

    const body = document.createElement("div");
    body.className = "hover-idle-mpm-section-body";

    const pauseOverlay = document.createElement("div");
    pauseOverlay.className = "hover-idle-mpm-section-pause-overlay";
    pauseOverlay.hidden = true;
    pauseOverlay.setAttribute("aria-hidden", "true");

    const pauseIcon = document.createElement("span");
    pauseIcon.className = "hover-idle-mpm-section-pause-icon";
    pauseIcon.innerHTML = pauseIconSource;
    pauseOverlay.append(pauseIcon);

    const blockedOverlay = document.createElement("div");
    blockedOverlay.className = "hover-idle-mpm-section-blocked-overlay";
    blockedOverlay.hidden = true;
    blockedOverlay.setAttribute("aria-hidden", "true");

    const blockedIcon = document.createElement("span");
    blockedIcon.className = "hover-idle-mpm-section-blocked-icon";
    blockedIcon.innerHTML = banIconSource;
    blockedOverlay.append(blockedIcon);

    body.append(options.row, pauseOverlay, blockedOverlay);
    section.append(header, body);
    return { element: section, pauseOverlay, blockedOverlay };
  }

  private createPresetSlot(presetIndex: number): MpmPresetSlotNodes {
    const messages = getMessages();
    const presetNumber = presetIndex + 1;
    const element = document.createElement("div");
    element.className = "hover-idle-mpm-preset-card";
    element.dataset.presetCard = "true";
    element.dataset.presetIndex = String(presetIndex);

    const ordinal = document.createElement("span");
    ordinal.className = "hover-idle-mpm-preset-ordinal";
    ordinal.textContent = String(presetNumber);

    const allocation = document.createElement("div");
    allocation.className = "hover-idle-mpm-preset-allocation";
    allocation.textContent = messages.mpm.presets.unsaved;

    const actions = document.createElement("div");
    actions.className = "hover-idle-mpm-preset-actions";
    const saveAction = this.createHeaderAction({
      section: "presets",
      action: "preset-save",
      variant: "save",
      label: messages.mpm.presets.save,
      ariaLabel: messages.mpm.presets.saveAriaLabel(presetNumber),
      onExecute: () => this.onPresetSave(presetIndex),
    });
    const loadAction = this.createHeaderAction({
      section: "presets",
      action: "preset-load",
      variant: "load",
      label: messages.mpm.presets.load,
      ariaLabel: messages.mpm.presets.loadAriaLabel(presetNumber),
      onExecute: () => this.onPresetLoad(presetIndex),
    });
    actions.append(saveAction.element, loadAction.element);
    element.append(ordinal, allocation, actions);
    return { element, allocation, saveAction, loadAction };
  }

  private applyPresetSlots(
    presets: readonly (MpmPresetSnapshot | null)[],
  ): void {
    const messages = getMessages();
    const availableSlotCount = Math.min(
      MPM_PRESET_MAX_SLOT_COUNT,
      Math.max(0, Math.trunc(this.getMpmPresetSlotCount())),
    );
    for (const [presetIndex, nodes] of this.presetSlots.entries()) {
      const available = presetIndex < availableSlotCount;
      const preset = presets[presetIndex] ?? null;
      nodes.element.hidden = !available;
      nodes.allocation.textContent =
        preset === null
          ? messages.mpm.presets.unsaved
          : messages.mpm.presets.assignedMp(computeMpmPresetAssignedMp(preset));
      nodes.saveAction.controller.sync({ disabled: !available });
      nodes.loadAction.controller.sync({
        disabled: !available || preset === null,
      });
      if (!available) {
        nodes.saveAction.controller.clearPointerHover();
        nodes.loadAction.controller.clearPointerHover();
      }
    }
  }

  private createHeaderAction(options: {
    readonly section: MpmHeaderActionSectionId;
    readonly action?:
      | "clear-all"
      | "assign-all"
      | "preset-save"
      | "preset-load";
    readonly variant?: "danger" | "positive" | "save" | "load";
    readonly label: string;
    readonly ariaLabel: string;
    readonly onExecute: () => void;
  }): MpmHeaderActionNodes {
    const action = options.action ?? "clear-all";
    const variant = options.variant ?? "danger";
    const button = document.createElement("button");
    button.type = "button";
    button.className = `hover-idle-mpm-header-action hover-idle-mpm-header-action--${variant}`;
    button.dataset.action = action;
    button.dataset.actionSection = options.section;
    button.dataset.variant = variant;
    button.dataset.virtualPointer = "allow";
    button.setAttribute("aria-label", options.ariaLabel);

    const fill = document.createElement("span");
    fill.className = "hover-idle-mpm-header-action-fill";
    fill.setAttribute("aria-hidden", "true");

    const label = document.createElement("span");
    label.className = "hover-idle-mpm-header-action-label";
    label.textContent = options.label;

    button.append(fill, label);

    return {
      element: button,
      controller: new MpmHeaderActionChargeController({
        element: button,
        pointerModel: this.pointerModel,
        getChargeMs: this.getHoverChargeDurationMs,
        onExecute: options.onExecute,
      }),
    };
  }

  private createEnabledToggle(options: {
    readonly section: MpmAssignmentSectionId;
    readonly onEnabledChange: (enabled: boolean) => void;
  }): MpmEnabledToggleNodes {
    return createEnabledHoverToggle({
      pointerModel: this.pointerModel,
      action: "enabled-toggle",
      actionSection: options.section,
      ariaLabel: "",
      getChargeMs: this.resolveChargeMsGetter(),
      onEnabledChange: options.onEnabledChange,
    });
  }

  // Shared getter that all small-square hover toggles in this view use so the
  // user's "hover toggle duration" setting applies uniformly.
  private resolveChargeMsGetter(): (() => number) | undefined {
    const getter = this.getHoverToggleDurationMs;
    return getter ? () => getter() : undefined;
  }

  private applySectionEnabledState(options: {
    readonly section: MpmAssignmentSectionNodes;
    readonly toggle: MpmEnabledToggleNodes;
    readonly title: string;
    readonly enabled: boolean;
  }): void {
    const messages = getMessages();
    options.section.element.dataset.enabled = options.enabled
      ? "true"
      : "false";
    options.section.pauseOverlay.hidden = options.enabled;
    options.toggle.sync({
      enabled: options.enabled,
      ariaLabel: options.enabled
        ? messages.mpm.sectionEnabledAriaLabel(options.title)
        : messages.mpm.sectionDisabledAriaLabel(options.title),
    });
  }

  private applySectionMpBlockedState(
    section: MpmAssignmentSectionNodes,
    mpBlocked: boolean,
    mpBlockedReason?: DirectMpBlockedReason,
  ): void {
    const showMpWallBlockedOverlay = mpBlocked && mpBlockedReason === "mp-wall";
    section.element.dataset.wallBlocked = mpBlocked ? "true" : "false";
    section.element.dataset.mpBlocked = mpBlocked ? "true" : "false";
    if (mpBlocked && mpBlockedReason !== undefined) {
      section.element.dataset.mpBlockedReason = mpBlockedReason;
    } else {
      delete section.element.dataset.mpBlockedReason;
    }
    section.blockedOverlay.hidden = !showMpWallBlockedOverlay;
    if (mpBlocked) {
      section.pauseOverlay.hidden = true;
    }
  }

  private createHovTargetSlot(index: number): MpmHovTargetSlotNodes {
    const labelText = `H${index + 1}`;
    const element = document.createElement("div");
    element.className = "hover-idle-mpm-hov-target";
    element.dataset.targetIndex = String(index);
    element.dataset.assigned = "false";
    element.dataset.mpAssigned = "false";
    element.dataset.mpOperative = "false";
    element.dataset.disabled = "false";
    element.tabIndex = 0;
    element.setAttribute("role", "button");
    element.hidden = true;

    const label = document.createElement("span");
    label.className = "hover-idle-mpm-hov-target-label";
    label.textContent = labelText;

    const indicator = createMpSlotIndicator();
    element.append(label, indicator);

    const nodes: MpmHovTargetSlotNodes = {
      index,
      element,
      indicator,
      controller: null as unknown as MpSlotChargeController,
      isAssigned: false,
      isDisabled: false,
    };

    (nodes as { controller: MpSlotChargeController }).controller =
      new MpSlotChargeController({
        element,
        stateElement: indicator,
        pointerModel: this.pointerModel,
        getChargeMs: this.resolveChargeMsGetter(),
        onAssign: () => this.onMpAssign(nodes.index),
        onRemove: () => this.onMpRemove(nodes.index),
      });

    return nodes;
  }

  private createHovGainUpgradeSlot(index: number): MpmHovGainUpgradeSlotNodes {
    const labelText = index === 0 ? "HG" : `H${index}G`;
    const element = document.createElement("div");
    element.className = "hover-idle-mpm-hov-gain-upgrade";
    element.dataset.upgradeIndex = String(index);
    element.dataset.assigned = "false";
    element.dataset.mpAssigned = "false";
    element.dataset.mpOperative = "false";
    element.dataset.disabled = "false";
    element.dataset.virtualPointer = "allow";
    element.tabIndex = 0;
    element.setAttribute("role", "button");
    element.hidden = true;

    const label = document.createElement("span");
    label.className = "hover-idle-mpm-hov-gain-upgrade-label";
    label.textContent = labelText;

    const indicator = createMpSlotIndicator();
    element.append(label, indicator);

    const nodes: MpmHovGainUpgradeSlotNodes = {
      index,
      element,
      indicator,
      controller: null as unknown as MpSlotChargeController,
      isAssigned: false,
      isDisabled: false,
      target: null,
    };

    (nodes as { controller: MpSlotChargeController }).controller =
      new MpSlotChargeController({
        element,
        stateElement: indicator,
        pointerModel: this.pointerModel,
        getChargeMs: this.resolveChargeMsGetter(),
        onAssign: () => {
          const target = nodes.target;
          if (target !== null) this.onHovGainUpgradeMpAssign(target);
        },
        onRemove: () => {
          const target = nodes.target;
          if (target !== null) this.onHovGainUpgradeMpRemove(target);
        },
      });

    return nodes;
  }

  private createAmplifierPanelSlot(
    entryId: HovAmplifierEntryId,
  ): MpmAmplifierPanelSlotNodes {
    const labelText = HOV_AMPLIFIER_SHORT_LABELS[entryId];
    const element = document.createElement("div");
    element.className = "hover-idle-mpm-amplifier-panel";
    element.dataset.amplifierEntryId = entryId;
    element.dataset.assigned = "false";
    element.dataset.disabled = "false";
    element.dataset.virtualPointer = "allow";
    element.tabIndex = 0;
    element.setAttribute("role", "button");
    element.hidden = true;

    const label = document.createElement("span");
    label.className = "hover-idle-mpm-amplifier-panel-label";
    label.textContent = labelText;

    const indicator = createMpSlotIndicator();
    element.append(label, indicator);

    const nodes: MpmAmplifierPanelSlotNodes = {
      entryId,
      element,
      indicator,
      controller: null as unknown as MpSlotChargeController,
      isAssigned: false,
      isDisabled: false,
    };

    (nodes as { controller: MpSlotChargeController }).controller =
      new MpSlotChargeController({
        element,
        stateElement: indicator,
        pointerModel: this.pointerModel,
        getChargeMs: this.resolveChargeMsGetter(),
        onAssign: () => this.onAmplifierPanelMpAssign(nodes.entryId),
        onRemove: () => this.onAmplifierPanelMpRemove(nodes.entryId),
      });

    return nodes;
  }

  private createAmplifierUpgradeSlot(
    entryId: HovAmplifierEntryId,
  ): MpmAmplifierUpgradeSlotNodes {
    const labelText = HOV_AMPLIFIER_SHORT_LABELS[entryId];
    const element = document.createElement("div");
    element.className = "hover-idle-mpm-amplifier-upgrade";
    element.dataset.amplifierEntryId = entryId;
    element.dataset.mpTargetKind = "amplifier-upgrade";
    element.dataset.assigned = "false";
    element.dataset.disabled = "false";
    element.dataset.virtualPointer = "allow";
    element.tabIndex = 0;
    element.setAttribute("role", "button");
    element.hidden = true;

    const label = document.createElement("span");
    label.className = "hover-idle-mpm-amplifier-upgrade-label";
    label.textContent = labelText;

    const indicator = createMpSlotIndicator();
    element.append(label, indicator);

    const nodes: MpmAmplifierUpgradeSlotNodes = {
      entryId,
      element,
      indicator,
      controller: null as unknown as MpSlotChargeController,
      isAssigned: false,
      isDisabled: false,
    };

    (nodes as { controller: MpSlotChargeController }).controller =
      new MpSlotChargeController({
        element,
        stateElement: indicator,
        pointerModel: this.pointerModel,
        getChargeMs: this.resolveChargeMsGetter(),
        onAssign: () => this.onAmplifierUpgradeMpAssign(nodes.entryId),
        onRemove: () => this.onAmplifierUpgradeMpRemove(nodes.entryId),
      });

    return nodes;
  }

  private createExplorationSlot(
    slot: ExplorationMpSlotDefinition,
  ): MpmExplorationSlotNodes {
    const messages = getMessages().exploration;
    const element = document.createElement("div");
    element.className = "hover-idle-mpm-exploration-slot";
    element.dataset.explorationMpSlot = slot.id;
    element.dataset.requiredMp = String(slot.amount);
    element.dataset.assigned = "false";
    element.dataset.mpAssigned = "false";
    element.dataset.mpOperative = "false";
    element.dataset.disabled = "false";
    element.dataset.virtualPointer = "allow";
    element.tabIndex = 0;
    element.setAttribute("role", "button");
    element.setAttribute(
      "aria-label",
      messages.assignMpSlotAria(String(slot.amount)),
    );
    element.hidden = true;

    const indicator = createMpSlotIndicator({ requiredMp: slot.amount });
    const missingMpTooltip = this.createMissingMpTooltip(element);
    element.append(indicator, missingMpTooltip.tooltip);

    const nodes: MpmExplorationSlotNodes = {
      slotId: slot.id,
      biomeId: slot.biomeId,
      requiredMp: slot.amount,
      element,
      indicator,
      controller: null as unknown as MpSlotChargeController,
      isAssigned: false,
      isDisabled: false,
      missingMpTooltip,
    };
    (nodes as { controller: MpSlotChargeController }).controller =
      new MpSlotChargeController({
        element,
        stateElement: indicator,
        pointerModel: this.pointerModel,
        getChargeMs: this.resolveChargeMsGetter(),
        onAssign: () => this.onExplorationMpAssign(nodes.slotId),
        onRemove: () => this.onExplorationMpRemove(nodes.slotId),
      });
    return nodes;
  }

  private createMHovExecutionSlot(): MpmMHovExecutionSlotNodes {
    const element = document.createElement("div");
    element.className =
      "hover-idle-mpm-mhov-execution hover-idle-mpm-mhov-execution-button";
    element.dataset.assignment = "mhov-execution";
    element.dataset.requiredMp = String(MPM_MHOV_EXECUTION_MP_COST);
    element.dataset.assigned = "false";
    element.dataset.disabled = "false";
    element.dataset.virtualPointer = "allow";
    element.tabIndex = 0;
    element.setAttribute("role", "button");
    element.setAttribute("aria-label", "MHov execution MP assignment");

    const indicator = createMpSlotIndicator({
      requiredMp: MPM_MHOV_EXECUTION_MP_COST,
    });
    const missingMpTooltip = this.createMissingMpTooltip(element);
    element.append(indicator, missingMpTooltip.tooltip);

    const nodes: MpmMHovExecutionSlotNodes = {
      element,
      indicator,
      controller: null as unknown as MpSlotChargeController,
      isAssigned: false,
      isDisabled: false,
      missingMpTooltip,
    };
    (nodes as { controller: MpSlotChargeController }).controller =
      new MpSlotChargeController({
        element,
        stateElement: indicator,
        pointerModel: this.pointerModel,
        getChargeMs: this.resolveChargeMsGetter(),
        onAssign: () => this.onMHovExecutionMpAssign(),
        onRemove: () => this.onMHovExecutionMpRemove(),
      });
    return nodes;
  }

  private createNHovExecutionBranchRow(
    branchId: NHovBranchId,
    messages: ReturnType<typeof getMessages>,
  ): {
    readonly element: HTMLDivElement;
    readonly slot: MpmNHovExecutionSlotNodes;
    readonly inputs: MpmExecutionConditionInputNodes;
  } {
    const element = document.createElement("div");
    element.className =
      "hover-idle-mpm-reset-condition-row hover-idle-mpm-nhov-execution-branch-row";
    element.dataset.branchConditionRow = branchId;

    const slot = this.createNHovExecutionSlot(branchId);
    const branchLabel = document.createElement("div");
    branchLabel.className =
      "hover-idle-mpm-reset-assignment-label hover-idle-mpm-nhov-execution-branch-label";
    branchLabel.textContent = this.resolveNHovBranchLabel(branchId, messages);

    const control = this.createResetAssignmentCell({
      className: "hover-idle-mpm-nhov-execution-branch-control",
      label: branchLabel,
      slot: slot.element,
    });

    const effect = this.createMpmExecutionInput({
      label: messages.mpm.nhovExecutionInputs.minimumEffect[branchId],
      saveAction: "nhov-execution-input-save",
      onSave: (value) => this.onNHovExecutionMinimumEffectSave(branchId, value),
    });
    const interval = this.createMpmExecutionInput({
      label: messages.mpm.nhovExecutionInputs.minimumResetIntervalSeconds,
      saveAction: "nhov-execution-input-save",
      onSave: (value) =>
        this.onNHovExecutionMinimumTimeSecondsSave(branchId, Number(value)),
    });

    element.append(control, effect.root, interval.root);
    return { element, slot, inputs: { effect, interval } };
  }

  private createResetAssignmentCell(options: {
    readonly className: string;
    readonly label?: HTMLElement;
    readonly slot: HTMLDivElement;
  }): HTMLDivElement {
    const cell = document.createElement("div");
    cell.className = `hover-idle-mpm-reset-assignment-cell ${options.className}`;
    const label = options.label ?? document.createElement("span");
    if (
      !label.className
        .split(/\s+/u)
        .includes("hover-idle-mpm-reset-assignment-label")
    ) {
      label.className =
        `${label.className} hover-idle-mpm-reset-assignment-label`.trim();
    }
    if (options.label === undefined) {
      label.className =
        `${label.className} hover-idle-mpm-reset-assignment-label--placeholder`.trim();
      label.setAttribute("aria-hidden", "true");
      label.textContent = "MP";
    }
    cell.append(options.slot, label);
    return cell;
  }

  private createNHovExecutionSlot(
    branchId: NHovBranchId,
  ): MpmNHovExecutionSlotNodes {
    const messages = getMessages();
    const element = document.createElement("div");
    element.className =
      "hover-idle-mpm-nhov-execution hover-idle-mpm-execution-assignment";
    element.dataset.assignment = "nhov-execution";
    element.dataset.branchId = branchId;
    element.dataset.requiredMp = String(MPM_NHOV_EXECUTION_MP_COST_PER_BRANCH);
    element.dataset.assigned = "false";
    element.dataset.disabled = "false";
    element.dataset.virtualPointer = "allow";
    element.tabIndex = 0;
    element.setAttribute("role", "button");
    element.setAttribute(
      "aria-label",
      `${this.resolveNHovBranchLabel(branchId, messages)} MP assignment`,
    );

    const indicator = createMpSlotIndicator({
      requiredMp: MPM_NHOV_EXECUTION_MP_COST_PER_BRANCH,
    });
    const missingMpTooltip = this.createMissingMpTooltip(element);
    element.append(indicator, missingMpTooltip.tooltip);

    const nodes: MpmNHovExecutionSlotNodes = {
      branchId,
      element,
      indicator,
      controller: null as unknown as MpSlotChargeController,
      isAssigned: false,
      isDisabled: false,
      missingMpTooltip,
    };
    (nodes as { controller: MpSlotChargeController }).controller =
      new MpSlotChargeController({
        element,
        stateElement: indicator,
        pointerModel: this.pointerModel,
        getChargeMs: this.resolveChargeMsGetter(),
        onAssign: () => this.onNHovExecutionMpAssign(nodes.branchId),
        onRemove: () => this.onNHovExecutionMpRemove(nodes.branchId),
      });
    return nodes;
  }

  private createOHovExecutionSlot(): MpmExecutionSlotNodes {
    const element = document.createElement("div");
    element.className =
      "hover-idle-mpm-ohov-execution hover-idle-mpm-execution-assignment";
    element.dataset.assignment = "ohov-execution";
    element.dataset.requiredMp = String(MPM_OHOV_EXECUTION_MP_COST);
    element.dataset.assigned = "false";
    element.dataset.disabled = "false";
    element.dataset.virtualPointer = "allow";
    element.tabIndex = 0;
    element.setAttribute("role", "button");
    element.setAttribute("aria-label", "OHov execution MP assignment");

    const indicator = createMpSlotIndicator({
      requiredMp: MPM_OHOV_EXECUTION_MP_COST,
    });
    const missingMpTooltip = this.createMissingMpTooltip(element);
    element.append(indicator, missingMpTooltip.tooltip);

    const nodes: MpmExecutionSlotNodes = {
      element,
      indicator,
      controller: null as unknown as MpSlotChargeController,
      isAssigned: false,
      isDisabled: false,
      missingMpTooltip,
    };
    (nodes as { controller: MpSlotChargeController }).controller =
      new MpSlotChargeController({
        element,
        stateElement: indicator,
        pointerModel: this.pointerModel,
        getChargeMs: this.resolveChargeMsGetter(),
        onAssign: () => this.onOHovExecutionMpAssign(),
        onRemove: () => this.onOHovExecutionMpRemove(),
      });
    return nodes;
  }

  private resolveNHovBranchLabel(
    branchId: NHovBranchId,
    messages: ReturnType<typeof getMessages>,
  ): string {
    return messages.hovView.nHov.branchHeading[branchId];
  }

  private createMissingMpTooltip(
    element: HTMLDivElement,
  ): MpmMissingMpTooltipNodes {
    const tooltip = document.createElement("div");
    tooltip.className =
      "hover-idle-tooltip-card hover-idle-mpm-missing-mp-tooltip";
    tooltip.setAttribute("role", "tooltip");
    tooltip.setAttribute("aria-hidden", "true");
    tooltip.hidden = true;

    const show = (): void => {
      if (element.dataset.missingMpTooltip !== "true" || element.hidden) {
        return;
      }
      tooltip.hidden = false;
      tooltip.setAttribute("aria-hidden", "false");
      this.positionMissingMpTooltip(element, tooltip);
    };
    const hide = (): void => {
      tooltip.hidden = true;
      tooltip.setAttribute("aria-hidden", "true");
    };
    const reposition = (): void => {
      if (!tooltip.hidden) this.positionMissingMpTooltip(element, tooltip);
    };

    element.addEventListener("pointerenter", show);
    element.addEventListener("focus", show);
    element.addEventListener("pointerleave", hide);
    element.addEventListener("blur", hide);
    const globalEventTarget = this.getGlobalTooltipEventTarget();
    globalEventTarget?.addEventListener("resize", reposition);
    globalEventTarget?.addEventListener("scroll", reposition, true);

    return {
      tooltip,
      cleanup: () => {
        element.removeEventListener("pointerenter", show);
        element.removeEventListener("focus", show);
        element.removeEventListener("pointerleave", hide);
        element.removeEventListener("blur", hide);
        globalEventTarget?.removeEventListener("resize", reposition);
        globalEventTarget?.removeEventListener("scroll", reposition, true);
        hide();
      },
    };
  }

  private getGlobalTooltipEventTarget(): Window | null {
    return typeof window === "undefined" ? null : window;
  }

  private positionMissingMpTooltip(
    element: HTMLElement,
    tooltip: HTMLElement,
  ): void {
    const rect = element.getBoundingClientRect();
    const viewportWidth =
      typeof window === "undefined"
        ? Number.POSITIVE_INFINITY
        : window.innerWidth;
    const x = Math.min(
      Math.max(12, rect.left + rect.width / 2),
      viewportWidth - 12,
    );
    tooltip.style.left = `${x}px`;
    tooltip.style.top = `${Math.max(12, rect.bottom + 8)}px`;
  }

  private createMpmExecutionInput(options: {
    readonly label: string;
    readonly saveAction: string;
    readonly onSave: (value: string) => void;
  }): MpmTextInputNodes {
    const root = document.createElement("div");
    root.className =
      "hover-idle-mpm-execution-input-control hover-idle-mpm-mhov-execution-input-control";
    const label = document.createElement("label");
    label.className =
      "hover-idle-mpm-execution-input-label hover-idle-mpm-mhov-execution-input-label";
    label.textContent = options.label;
    const input = document.createElement("input");
    input.className =
      "hover-idle-mpm-execution-input hover-idle-mpm-mhov-execution-input";
    input.type = "text";
    input.inputMode = "decimal";
    input.setAttribute("aria-label", options.label);
    input.readOnly = true;
    const saveButton = document.createElement("button");
    saveButton.type = "button";
    saveButton.className =
      "hover-idle-mpm-execution-save hover-idle-mpm-mhov-execution-save";
    saveButton.dataset.action = options.saveAction;
    saveButton.setAttribute("aria-label", `Save ${options.label}`);
    saveButton.innerHTML = saveIconSource;
    saveButton.hidden = true;
    const nodes: MpmTextInputNodes = {
      root,
      label,
      input,
      saveButton,
      savedValue: "",
      editing: false,
    };
    root.dataset.editing = "false";
    const beginEdit = (): void => {
      if (!isHoverIdleDocumentFocused()) return;
      if (nodes.editing) return;
      nodes.editing = true;
      root.dataset.editing = "true";
      input.readOnly = false;
      saveButton.hidden = false;
      input.focus();
      input.select();
    };
    const endEdit = (): void => {
      nodes.editing = false;
      root.dataset.editing = "false";
      input.readOnly = true;
      saveButton.hidden = true;
      if (typeof input.setSelectionRange === "function") {
        try {
          input.setSelectionRange(0, 0);
        } catch {
          // Some browsers reject selection range on non-selectable inputs.
        }
      }
      if (
        typeof input.blur === "function" &&
        typeof document !== "undefined" &&
        document.activeElement === input
      ) {
        input.blur();
      }
    };
    const save = (): void => {
      endEdit();
      options.onSave(input.value);
    };
    input.addEventListener("pointerenter", beginEdit);
    input.addEventListener("focus", beginEdit);
    root.addEventListener("pointerleave", () => {
      if (input === document.activeElement) {
        return;
      }
      endEdit();
    });
    input.addEventListener("blur", () => {
      setTimeout(() => {
        if (saveButton === document.activeElement) {
          return;
        }
        endEdit();
      }, 0);
    });
    saveButton.addEventListener("pointerenter", save);
    saveButton.addEventListener("touchstart", save);
    saveButton.addEventListener("click", save);
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        save();
        input.blur();
      }
    });
    root.append(label, input, saveButton);
    return nodes;
  }

  private formatMHovExecutionIntervalSeconds(valueMs: number): string {
    return String(valueMs * MPM_MHOV_EXECUTION_INTERVAL_SECONDS_PER_MS);
  }

  private applyTextInputSavedValue(
    nodes: MpmTextInputNodes,
    savedValue: string,
  ): void {
    const previousSavedValue = nodes.savedValue;
    nodes.savedValue = savedValue;
    if (!nodes.editing || savedValue !== previousSavedValue) {
      nodes.input.value = savedValue;
    }
  }

  private applySlotState(
    nodes: MpmAssignableSlotNodes,
    assigned: boolean,
    disabled: boolean,
    options: {
      readonly operative?: boolean;
      readonly mpBlocked?: boolean;
      readonly mpBlockedReason?: DirectMpBlockedReason;
    } = {},
  ): void {
    const mpBlocked = options.mpBlocked === true;
    const hasMpBlockedState =
      mpBlocked ||
      nodes.element.dataset.mpBlocked !== undefined ||
      nodes.indicator.dataset.mpBlocked !== undefined;
    nodes.isAssigned = assigned;
    nodes.isDisabled = disabled;
    nodes.element.dataset.assigned = assigned ? "true" : "false";
    if (nodes.element.dataset.mpAssigned !== undefined) {
      nodes.element.dataset.mpAssigned = assigned ? "true" : "false";
      nodes.element.dataset.mpOperative =
        options.operative === true ? "true" : "false";
      nodes.indicator.dataset.mpAssigned = assigned ? "true" : "false";
      nodes.indicator.dataset.mpOperative =
        options.operative === true ? "true" : "false";
    }
    nodes.element.dataset.disabled = disabled && !assigned ? "true" : "false";
    nodes.indicator.dataset.disabled = disabled && !assigned ? "true" : "false";
    if (hasMpBlockedState) {
      nodes.element.dataset.mpBlocked = mpBlocked ? "true" : "false";
      nodes.indicator.dataset.mpBlocked = mpBlocked ? "true" : "false";
      if (mpBlocked && options.mpBlockedReason !== undefined) {
        nodes.element.dataset.mpBlockedReason = options.mpBlockedReason;
        nodes.indicator.dataset.mpBlockedReason = options.mpBlockedReason;
      } else {
        delete nodes.element.dataset.mpBlockedReason;
        delete nodes.indicator.dataset.mpBlockedReason;
      }
      nodes.element.dataset.blocked = mpBlocked ? "true" : "false";
      nodes.indicator.dataset.blocked = mpBlocked ? "true" : "false";
    }
    nodes.controller.sync({
      isAssigned: assigned,
      canAssign: !disabled,
      canRemove: true,
    });
  }

  private applyMissingMpTooltip(
    nodes: MpmExecutionSlotNodes,
    options: {
      readonly requiredMp: number;
      readonly availableMp: number;
      readonly shouldShow: boolean;
    },
  ): void {
    const messages = getMessages();
    const { element } = nodes;
    const missingMp = Math.max(
      0,
      Math.trunc(options.requiredMp) - Math.trunc(options.availableMp),
    );
    if (!options.shouldShow || missingMp <= 0) {
      delete element.dataset.missingMp;
      delete element.dataset.missingMpTooltip;
      delete element.dataset.missingMpTooltipContent;
      nodes.missingMpTooltip.tooltip.textContent = "";
      nodes.missingMpTooltip.tooltip.hidden = true;
      nodes.missingMpTooltip.tooltip.setAttribute("aria-hidden", "true");
      element.setAttribute("title", "");
      return;
    }
    const content = messages.mpm.assignmentTooltips.missingMp(missingMp);
    element.dataset.missingMp = String(missingMp);
    element.dataset.missingMpTooltip = "true";
    element.dataset.missingMpTooltipContent = content;
    nodes.missingMpTooltip.tooltip.textContent = content;
    element.setAttribute("title", "");
  }

  private applyMpAvailability(available: number, capacity: number): void {
    const messages = getMessages();
    this.mpAvailabilityHudValue.textContent = `${available} / ${capacity} MP`;
    this.mpAvailabilityHud.dataset.available = String(available);
    this.mpAvailabilityHud.dataset.capacity = String(capacity);
    this.mpAvailabilityHud.setAttribute(
      "aria-label",
      messages.mpm.mpAvailabilityAriaLabel(String(available), String(capacity)),
    );
  }
}
